/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2025 (original work) Open Assessment Technologies SA;
 */

import { __ } from '@oat-sa-private/ui-core';

let storedLangCode;

// values must be paths in app's locale folder
const localeFallbacks = {
    'ca': 'val-ES',
    'de': 'de-DE',
    'en': 'en-US',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'nl': 'nl-NL',
    'pt': 'pt-BR',
    'val': 'es-ES',
};

export const appLocaleFallback = 'en-US';

/**
 * Normalize locale codes to the format used in the locale folders.
 * @param {string} localeCode
 * @returns {string}
 */
export function normalizeLocaleCode(localeCode = appLocaleFallback) {
    const [lang, region, ...rest] = localeCode.split('-');
    return [lang?.toLowerCase(), region?.toUpperCase(), ...rest].filter(Boolean).join('-');
}

/**
 * Encapsulates functions to set the locale
 */
export const locale = {
    /**
     *
     * @param {String} requestedLocale
     * @returns {Promise<module>}
     */
    loadDictionary(requestedLocale) {
        return import(`../../locale/${requestedLocale}/messages.po`);
    },

    /**
     * Get a fallback locale for a given locale
     * Useful for filling missing or partial locale dictionaries in i18n module
     * @example getLocaleFallback('es-MX') // => 'es-ES'
     *
     * @param {string} langCode in BCP47 format
     * @returns {string} another locale
     */
    getLocaleFallback(langCode = 'en-US') {
        const localePrefix = langCode.split('-')[0].toLowerCase();
        return localeFallbacks[localePrefix] || 'en-US';
    },

    /**
     * Set the application locale.
     * @async
     * @param {String} langCode - unified language key.
     * @returns {void}
     */
    async setApplicationLocale(langCode) {
        storedLangCode = langCode;
        await __.setLocale(langCode);
        await __.setP11nRules(langCode);

        const fallbackLangCode = this.getLocaleFallback(langCode);
        await __.setFallbackLocale(fallbackLangCode);
    },

    /**
     * Get the application locale.
     *
     * @returns {String} locale - previously set locale.
     */
    getApplicationLocale() {
        return storedLangCode;
    },

    /**
     * Sets the lang attribute on html tag
     *
     * @param {String} langCode - unified language key.
     */
    setLangAttribute(langCode) {
        document.documentElement.setAttribute('lang', langCode);
    }
};
